"use client";

/**
 * MUI DatePicker wrapped for Book4 theme and native form integration.
 * Outputs date as YYYY-MM-DD string for form state.
 */
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs/index.js";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { type Dayjs } from "dayjs";
import type { ReactNode } from "react";

type DatePickerFieldProps = {
  value: string; // YYYY-MM-DD
  onChange: (date: string) => void;
  minDate?: string; // YYYY-MM-DD
  label?: string;
  required?: boolean;
  disabled?: boolean;
  /** If provided, dates for which this returns true will be disabled (e.g. blocked dates). */
  shouldDisableDate?: (dateStr: string) => boolean;
  slotProps?: {
    textField?: {
      size?: "small" | "medium";
      placeholder?: string;
      required?: boolean;
      error?: boolean;
      helperText?: ReactNode;
    };
  };
};

export function DatePickerField({
  value,
  onChange,
  minDate,
  label = "Date",
  required,
  disabled,
  shouldDisableDate,
  slotProps,
}: DatePickerFieldProps) {
  const dateValue = value ? dayjs(value) : null;
  const minDateDayjs = minDate ? dayjs(minDate) : undefined;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePicker
        label={label}
        value={dateValue}
        minDate={minDateDayjs}
        disabled={disabled}
        shouldDisableDate={
          shouldDisableDate
            ? (dayjsDate: Dayjs) => shouldDisableDate(dayjsDate.format("YYYY-MM-DD"))
            : undefined
        }
        onChange={(newValue: Dayjs | null) => {
          onChange(newValue ? newValue.format("YYYY-MM-DD") : "");
        }}
        slotProps={
          {
            ...slotProps,
            textField: {
              size: "small",
              required,
              ...slotProps?.textField,
              sx: {
                "& .MuiOutlinedInput-root": {
                  borderRadius: "0.375rem",
                  backgroundColor: "var(--background)",
                  "& fieldset": {
                    borderColor: "var(--border)",
                  },
                  "&:hover fieldset": {
                    borderColor: "var(--primary)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "var(--primary)",
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputLabel-root": {
                  "&.Mui-focused": {
                    color: "var(--primary)",
                  },
                },
              },
            },
          } as Parameters<typeof DatePicker>[0]["slotProps"]
        }
      />
    </LocalizationProvider>
  );
}
